import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { SystemIcon } from "../ui";
import "./Layout.css";

const MODULE_TITLES = {
  dashboard: "대시보드",
  hr: "인사관리",
  accounting: "정보관리",
  inbound: "입고관리",
  inventory: "재고관리",
  orders: "생산관리",
  sales: "영업관리",
  stocktaking: "재고조사",
  dispatch: "출고관리",
  cs: "CS관리",
  traceability: "이력제관리",
};

const ITEMS_BY_MODULE = {
  dashboard: [
    {
      group: "main",
      title: "개요",
      links: [{ to: "/dashboard", label: "대시보드" }],
    },
  ],
  hr: [
    {
      group: "employee",
      title: "직원관리",
      links: [
        { to: "/hr", label: "직원목록" },
        { to: "/hr/add", label: "직원등록" },
      ],
    },
  ],
  accounting: [
    {
      group: "customer-info",
      title: "고객사정보",
      links: [
        { to: "/accounting/customer-info", label: "고객사정보" },
        { to: "/accounting/customer-info/excel", label: "엑셀입력" },
      ],
    },
    {
      group: "farm-info",
      title: "농장정보",
      links: [
        { to: "/accounting/farm-info", label: "농장정보" },
        { to: "/accounting/farm-info/age-status", label: "주령현황" },
        { to: "/accounting/farm-info/excel", label: "엑셀입력" },
      ],
    },
    {
      group: "factory-info",
      title: "공장정보",
      links: [
        { to: "/accounting/factory-info", label: "공장정보" },
        { to: "/accounting/factory-info/pallet-locations", label: "파레트 위치" },
      ],
    },
    {
      group: "product-info",
      title: "제품정보",
      links: [
        { to: "/accounting/product-info", label: "제품정보" },
        { to: "/accounting/product-subsidiary-usage", label: "제품별 필요 부자재" },
        { to: "/accounting/product-info/excel", label: "제품정보 엑셀입력" },
        { to: "/accounting/product-subsidiary-usage/excel", label: "제품부자재 엑셀입력" },
      ],
    },
    {
      group: "subsidiary-materials-info",
      title: "부자재정보",
      links: [
        { to: "/accounting/subsidiary-materials-companies", label: "부자재업체정보" },
        { to: "/accounting/subsidiary-materials", label: "부자재정보" },
        { to: "/accounting/subsidiary-materials-companies/excel", label: "부자재업체 엑셀입력" },
        { to: "/accounting/subsidiary-materials/excel", label: "부자재정보 엑셀입력" },
      ],
    },
  ],
  inbound: [
    {
      group: "egg-inbound",
      title: "계란입고관리",
      links: [
        { to: "/inbound/egg", label: "계란입고" },
        { to: "/inbound/egg/history", label: "변경내역" },
        { to: "/inbound/egg/excel", label: "엑셀입력" },
      ],
    },
    {
      group: "materials-inbound",
      title: "부자재입고관리",
      links: [
        { to: "/inbound/submaterials", label: "부자재입고" },
        { to: "/inbound/submaterials/history", label: "변경내역" },
        { to: "/inbound/submaterials/excel", label: "엑셀입력" },
      ],
    },
  ],
  inventory: [
    {
      group: "egg",
      title: "계란재고",
      links: [
        { to: "/inventory/egg-inventory", label: "계란재고" },
        { to: "/inventory/egg-inventory/locations", label: "계란재고위치관리" },
        { to: "/inventory/egg-inventory/daily-stock", label: "계란재고전일표" },
        { to: "/inventory/egg-inventory/history", label: "변경내역" },
        { to: "/inventory/egg-inventory/excel", label: "엑셀입력" },
      ],
    },
    {
      group: "goods",
      title: "제품재고",
      links: [
        { to: "/inventory/goods-inventory", label: "제품재고" },
        { to: "/inventory/goods-inventory/daily-stock", label: "제품재고전일표" },
        { to: "/inventory/goods-inventory/history", label: "변경내역" },
        { to: "/inventory/goods-inventory/excel", label: "엑셀입력" },
      ],
    },
    {
      group: "materials",
      title: "부자재재고",
      links: [
        { to: "/inventory/materials-inventory", label: "부자재재고" },
        { to: "/inventory/materials-inventory/history", label: "변경내역" },
        { to: "/inventory/materials-inventory/excel", label: "엑셀입력" },
      ],
    },
  ],
  orders: [
    {
      group: "fresh-production-management",
      title: "생란생산관리",
      links: [
        { to: "/orders/fresh/quantity-confirmation", label: "생산수량확정" },
        { to: "/orders/fresh/egg-matching", label: "원란지정" },
        { to: "/orders/fresh/instructions", label: "작업지시관리" },
        { to: "/orders/fresh/instruction-completion", label: "작업지시완료" },
        { to: "/orders/fresh/instruction-history", label: "작업지시내역" },
      ],
    },
    {
      group: "baked-production-management",
      title: "구운란생산관리",
      links: [
        { to: "/orders/baked/quantity-confirmation", label: "생산수량확정" },
        { to: "/orders/baked/egg-matching", label: "원란지정" },
        { to: "/orders/baked/instructions", label: "작업지시관리" },
        { to: "/orders/baked/instruction-completion", label: "작업지시완료" },
        { to: "/orders/baked/instruction-history", label: "작업지시내역" },
      ],
    },
    {
      group: "liquid-production-management",
      title: "액란생산관리",
      links: [
        { to: "/orders/liquid/quantity-confirmation", label: "생산수량확정" },
        { to: "/orders/liquid/egg-matching", label: "원란지정" },
        { to: "/orders/liquid/instructions", label: "작업지시관리" },
        { to: "/orders/liquid/instruction-completion", label: "작업지시완료" },
        { to: "/orders/liquid/instruction-history", label: "작업지시내역" },
      ],
    },
    {
      group: "disposal-out-of-grade-management",
      title: "생산/폐기/등외란관리",
      links: [
        { to: "/orders/disposal-out-of-grade/production", label: "생산내역" },
        { to: "/orders/disposal-out-of-grade/disposals", label: "폐기내역" },
        { to: "/orders/disposal-out-of-grade/out-of-grade", label: "등외란내역" },
        { to: "/orders/disposal-out-of-grade/baked-washed", label: "구운란내역" },
      ],
    },
  ],
  sales: [
    {
      group: "sales-management",
      title: "영업관리",
      links: [
        { to: "/sales/inbound-outbound-status", label: "입출고현황" },
        { to: "/sales/target-performance", label: "목표대비실적" },
        { to: "/sales/sales-status", label: "매출현황" },
        { to: "/sales/product-prices", label: "제품단가" },
      ],
    },
  ],
  stocktaking: [
    {
      group: "stocktaking-management",
      title: "재고조사",
      links: [{ to: "/stocktaking/sessions", label: "재고조사" }],
    },
    {
      group: "stocktaking-record-management",
      title: "분실/습득 관리",
      links: [
        { to: "/stocktaking/losses", label: "분실내역" },
        { to: "/stocktaking/founds", label: "습득내역" },
      ],
    },
  ],
  dispatch: [
    {
      group: "order-management",
      title: "발주관리",
      links: [
        { to: "/dispatch/order-management", label: "발주" },
        { to: "/dispatch/order-history", label: "변경내역" },
        { to: "/dispatch/order-excel", label: "엑셀입력" },
      ],
    },
    {
      group: "dispatch-management",
      title: "출고관리",
      links: [
        { to: "/dispatch/management", label: "출고관리" },
        { to: "/dispatch/history", label: "출고내역" },
      ],
    },
  ],
  cs: [
    {
      group: "cs-management",
      title: "CS관리",
      links: [
        { to: "/cs/manage", label: "CS관리" },
        { to: "/cs/history", label: "CS관리변경내역" },
      ],
    },
  ],
  traceability: [
    {
      group: "traceability-management",
      title: "이력제관리",
      links: [
        { to: "/traceability/inbound", label: "입고이력" },
        { to: "/traceability/packing", label: "선별포장실적신고" },
        { to: "/traceability/packing-processed", label: "선별포장실적신고(알가공품)" },
        { to: "/traceability/outbound", label: "출고이력" },
      ],
    },
  ],
};

const shouldEndMatch = (links, currentTo) =>
  links.some((other) => other.to !== currentTo && other.to.startsWith(currentTo + "/"));

export default function Sidebar() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [openSet, setOpenSet] = useState(() => new Set());
  const isAdmin = user?.isAdmin === true || String(user?.role || "").toUpperCase() === "ADMIN";

  const moduleKey = useMemo(() => {
    if (pathname.startsWith("/hr")) return "hr";
    if (pathname.startsWith("/accounting")) return "accounting";
    if (pathname.startsWith("/inbound")) return "inbound";
    if (pathname.startsWith("/inventory")) return "inventory";
    if (pathname.startsWith("/sales")) return "sales";
    if (pathname.startsWith("/stocktaking")) return "stocktaking";
    if (pathname.startsWith("/traceability")) return "traceability";
    if (pathname.startsWith("/dispatch")) return "dispatch";
    if (pathname.startsWith("/cs")) return "cs";
    if (pathname.startsWith("/shipping")) return "dispatch";
    if (pathname.startsWith("/orders")) return "orders";
    return "dashboard";
  }, [pathname]);

  useEffect(() => {
    setOpenSet(new Set());
  }, [moduleKey]);

  const toggleGroup = (group) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const moduleTitle = MODULE_TITLES[moduleKey] || "";
  const sections = useMemo(() => {
    const baseSections = ITEMS_BY_MODULE[moduleKey] || [];

    if (moduleKey !== "hr" || isAdmin) return baseSections;

    return baseSections
      .map((section) => ({
        ...section,
        links: section.links.filter((link) => link.to !== "/hr/add"),
      }))
      .filter((section) => section.links.length > 0);
  }, [moduleKey, isAdmin]);

  return (
    <aside className="sidebar">
      <div className="sidebar-header">{moduleTitle}</div>

      {sections.map((sec) => {
        const isOpen = openSet.has(sec.group);
        return (
          <div key={sec.group} className={`sidebar-section ${isOpen ? "open" : ""}`}>
            <button
              type="button"
              className="sidebar-title"
              onClick={() => toggleGroup(sec.group)}
              aria-expanded={isOpen}
            >
              <span>{sec.title}</span>
              <SystemIcon
                name="angle"
                size="xsmall"
                direction={isOpen ? "up" : undefined}
                className="sidebar-caret"
              />
            </button>

            {isOpen && (
              <div className="sidebar-links">
                {sec.links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    className={({ isActive }) => (isActive ? "active" : "")}
                    end={shouldEndMatch(sec.links, l.to)}
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </aside>
  );
}
